import React from 'react';
import { Paper, Box, Divider, Slide } from '@mui/material';
import ChatHeader from './ChatHeader';
import LoadingState from './LoadingState';
import UserInfoForm from './UserInfoForm';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { ChatMessage, ChatSession } from '../../../services/chatSupportService';
import { WebSocketConnectionState } from '../../../services/chatWebSocketService';
import { UserInfoFormData, MessageFormData } from '../validationSchemas';

interface ChatWindowProps {
  isOpen: boolean;
  isMinimized: boolean;
  isInitializing: boolean;
  isUserInfoSubmitted: boolean;
  currentSession: ChatSession | null;
  messages: ChatMessage[];
  isTyping: boolean;
  showConnectionNotice: boolean;
  showScrollToBottom: boolean;
  isSubmittingMessage: boolean;
  connectionState: WebSocketConnectionState;
  userInfoMethods: any;
  messageMethods: any;
  initializeChatSessionMutation: any;
  sendMessageMutation: any;
  onMinimize: () => void;
  onClose: () => void;
  onUserInfoSubmit: (data: UserInfoFormData) => void;
  onSendMessage: (data: MessageFormData) => void;
  onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onScroll: () => void;
  onScrollToBottom: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  isOpen,
  isMinimized,
  isInitializing,
  isUserInfoSubmitted,
  currentSession,
  messages,
  isTyping,
  showConnectionNotice,
  showScrollToBottom,
  isSubmittingMessage,
  connectionState,
  userInfoMethods,
  messageMethods,
  initializeChatSessionMutation,
  sendMessageMutation,
  onMinimize,
  onClose,
  onUserInfoSubmit,
  onSendMessage,
  onKeyPress,
  onScroll,
  onScrollToBottom,
}) => {
  return (
    <Slide direction="up" in={isOpen} timeout={300}>
      <Paper className={`chat-window ${isMinimized ? 'minimized' : ''}`}>
        {/* Chat Header */}
        <ChatHeader
          isUserInfoSubmitted={isUserInfoSubmitted}
          connectionState={connectionState}
          onMinimize={onMinimize}
          onClose={onClose}
        />

        {!isMinimized && (
          <>
            <Divider />
            
            {/* Chat Content */}
            <Box className="chat-content">
              {isInitializing ? (
                <LoadingState />
              ) : !isUserInfoSubmitted ? (
                <UserInfoForm
                  formMethods={userInfoMethods}
                  isPending={initializeChatSessionMutation.isPending}
                  onSubmit={onUserInfoSubmit}
                />
              ) : (
                <ChatMessages
                  messages={messages}
                  isTyping={isTyping}
                  showConnectionNotice={showConnectionNotice}
                  showScrollToBottom={showScrollToBottom}
                  onScroll={onScroll}
                  onScrollToBottom={onScrollToBottom}
                />
              )}
            </Box>

            {isUserInfoSubmitted && (
              <>
                <Divider />
                <ChatInput
                  formMethods={messageMethods}
                  isPending={sendMessageMutation.isPending || isSubmittingMessage}
                  onSubmit={onSendMessage}
                  onKeyPress={onKeyPress}
                />
              </>
            )}
          </>
        )}
      </Paper>
    </Slide>
  );
};

export default ChatWindow;
